/**
 * Atom node factory for building ARIA tree structure
 *
 * @package @alchemiesolns/svg-interactive
 */

import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { nameAtom } from '@alchemiesolns/feature-extractor';
import type { AtomLeaf, BondLeaf, BondEndpointLeaf, LonePairLeaf } from './types';
import { findBondedHydrogenIds, createLonePairNodes } from './atomUtils';
import { findBondBetween, createBondLeafForAtom } from './bondUtils';

/**
 * Create an AtomLeaf with nested lone pairs and hydrogen children
 *
 * @param atomId - Original atom ID from workspace (e.g., "atom-1")
 * @param treeId - Tree navigation ID, may be feature-scoped (e.g., "feature-1:atom-1") or same as atomId
 * @param symbol - Element symbol
 * @param charge - Formal charge
 * @param lonePairCount - Number of lone pairs
 * @param lonePairsById - Map to register lone pairs
 * @param bondsById - Map to register bonds
 * @param endpointsById - Map to register bond endpoints
 * @param parentById - Map to register parent relationships
 * @param parentNodeId - ID of parent node in tree
 * @param workspace - Molecular workspace for hydrogen lookup
 * @param atomsById - Map to register atom leaves
 * @param elementIndex - 1-based index of this atom among same elements in feature (optional)
 * @param totalOfElement - Total count of this element type in feature (optional)
 */
export function createAtomLeaf(
  atomId: string,
  treeId: string,
  symbol: string,
  charge: number | undefined,
  lonePairCount: number,
  lonePairsById: Map<string, LonePairLeaf>,
  bondsById: Map<string, BondLeaf>,
  endpointsById: Map<string, BondEndpointLeaf>,
  parentById: Map<string, string>,
  parentNodeId: string,
  workspace: MolecularWorkspace,
  atomsById: Map<string, AtomLeaf>,
  elementIndex?: number,
  totalOfElement?: number
): AtomLeaf {
  // Use treeId as prefix for lone pairs to keep them scoped to this atom instance
  const lonePairNodes = createLonePairNodes(
    treeId,  // Use treeId so lone pairs are scoped to this atom instance
    symbol,
    lonePairCount,
    lonePairsById,
    parentById
  );

  // Find and create hydrogen children (only for non-hydrogen atoms)
  const hydrogenNodes: AtomLeaf[] = [];
  if (symbol !== 'H') {
    const hydrogenIds = findBondedHydrogenIds(workspace, atomId);
    const totalHydrogens = hydrogenIds.length;

    for (let hIndex = 0; hIndex < hydrogenIds.length; hIndex++) {
      const hId = hydrogenIds[hIndex];
      const hAtom = workspace.getAtom(hId);
      if (!hAtom) continue;

      // Create feature-scoped ID for hydrogen (inherits parent's scope)
      const hTreeId = treeId === atomId ? hId : `${treeId.split(':')[0]}:${hId}`;

      // Create hydrogen leaf (hydrogen's parent is this atom)
      const hLonePairNodes = createLonePairNodes(
        hTreeId,
        'H',
        hAtom.lonePairs || 0,
        lonePairsById,
        parentById
      );

      // PIPH-74: Check if hydrogen has stereo context (wedge/dash bond to parent)
      const hBond = findBondBetween(workspace, atomId, hId);
      const hHasStereoContext = hBond?.bond.bondType === 'w' || hBond?.bond.bondType === 'd';

      const hLeaf: AtomLeaf = {
        type: 'atom',
        id: hTreeId,
        atomId: hId,
        symbol: 'H',
        label: nameAtom(
          'H',
          hAtom.charge,
          hAtom.lonePairs,
          totalHydrogens > 1 ? hIndex + 1 : undefined,
          totalHydrogens
        ),
        charge: hAtom.charge || undefined,
        lonePairCount: hAtom.lonePairs || undefined,
        elementIndex: totalHydrogens > 1 ? hIndex + 1 : undefined,
        totalOfElement: totalHydrogens,
        x: hAtom.x,
        y: hAtom.y,
        hasStereoContext: hHasStereoContext || undefined,
        lonePairNodes: hLonePairNodes,
        hydrogenNodes: [], // Hydrogens don't have hydrogen children
        bondNodes: [],  // Will be populated later if needed
      };

      parentById.set(hTreeId, treeId);
      atomsById.set(hTreeId, hLeaf);
      hydrogenNodes.push(hLeaf);
    }
  }

  // PIPH-74: Get atom position for stereo bond direction calculation
  // This must be done BEFORE creating bonds so we can pass positions for direction labels
  const atomData = workspace.getAtom(atomId);
  const x = atomData?.x;
  const y = atomData?.y;

  // PIPH-74: Check if this atom has any wedge or dash bonds (stereo context)
  // When true, templates should describe bond directions for ALL bonds on this atom
  let hasStereoContext = false;
  for (const bond of workspace.getAllBonds()) {
    if (bond.a1.id === atomId || bond.a2.id === atomId) {
      if (bond.bondType === 'w' || bond.bondType === 'd') {
        hasStereoContext = true;
        break;
      }
    }
  }

  // Create bonds to hydrogens as children of the parent atom
  const hydrogenBondNodes: BondLeaf[] = [];
  if (symbol !== 'H') {
    for (const hLeaf of hydrogenNodes) {
      const bondResult = findBondBetween(workspace, atomId, hLeaf.atomId);
      if (bondResult) {
        const bondLeaf = createBondLeafForAtom(
          bondResult.bondId,
          atomId, // parentAtomId
          hLeaf.atomId, // otherAtomId (hydrogen)
          symbol, // parentAtomSymbol
          'H', // otherAtomSymbol
          bondResult.bond.order,
          bondResult.bond.bondType,
          treeId, // treeIdPrefix
          bondsById,
          endpointsById,
          parentById,
          treeId, // parentTreeId
          elementIndex, // parent atom index for endpoint label
          totalOfElement, // parent atom total for endpoint label
          hLeaf.elementIndex, // hydrogen index for bond/endpoint label
          hLeaf.totalOfElement, // total hydrogens for bond/endpoint label
          x, // PIPH-74: parent atom X position
          y, // PIPH-74: parent atom Y position
          hLeaf.x, // PIPH-74: other atom X position
          hLeaf.y, // PIPH-74: other atom Y position
          hasStereoContext // PIPH-74: include direction in label
        );
        hydrogenBondNodes.push(bondLeaf);
      }
    }
  }

  const leaf: AtomLeaf = {
    type: 'atom',
    id: treeId,
    atomId,
    symbol,
    label: nameAtom(symbol, charge, lonePairCount, elementIndex, totalOfElement),
    charge: charge || undefined,
    lonePairCount: lonePairCount || undefined,
    elementIndex,
    totalOfElement,
    x,
    y,
    hasStereoContext: hasStereoContext || undefined,
    lonePairNodes,
    hydrogenNodes,
    bondNodes: hydrogenBondNodes, // Initialize with bonds to hydrogens
  };

  parentById.set(treeId, parentNodeId);
  return leaf;
}
